import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Contacts from './pages/Contacts'
import LeadTasksBoard from './pages/LeadTasksBoard'
import Admin from './pages/Admin'
import Settings from './pages/Settings'
import AccountSettings from './pages/AccountSettings'
import LeadChats from './pages/LeadChats'
import BroadcastQueue from './pages/BroadcastQueue'
import Duplicates from './pages/Duplicates'
// РАЗДЕЛЫ ОТКЛЮЧЕНЫ вместе со статусной моделью: файлы страниц оставлены для возврата.
// import Calendar from './pages/Calendar'
import Export from './pages/Export'
import Statistics from './pages/Statistics'
import EmailCampaigns from './pages/email/Campaigns'
import Layout from './components/Layout/Layout'
import { managesSettings, seesAllLeads } from './constants/roles'
import { useRolesStore } from './store/rolesStore'

// Разделы, закрытые от оператора. Прятать их только в меню мало: по прямой
// ссылке страница открывалась бы и тянула данные, которых оператор видеть не
// должен.
function AdminOnly({ children }: { children: React.ReactNode }) {
    const { user } = useAuthStore()
    const isAdmin = seesAllLeads(user?.role)
    if (isAdmin) return <>{children}</>
    return <Navigate to={user?.can_view_dashboard === false ? '/chats' : '/'} replace />
}

// Настройки - только с правом на настройки. По прямой ссылке без него
// открывалась бы пустая страница «нет доступных настроек».
function SettingsOnly({ children }: { children: React.ReactNode }) {
    const { user } = useAuthStore()
    if (managesSettings(user?.role)) return <>{children}</>
    return <Navigate to="/chats" replace />
}

// Разделы, которые тимлид может скрыть от операторов в настройках. Уходить
// отсюда надо в «Чаты», а не на главную: главная сама может быть закрыта.
function AllowedRoute({ allowed, children }: { allowed?: boolean; children: React.ReactNode }) {
    return allowed === false ? <Navigate to="/chats" replace /> : <>{children}</>
}

function App() {
    const { isAuthenticated, fetchUser, user } = useAuthStore()
    // Подписка на справочник ролей: пришёл или поменялся - всё приложение
    // перерисовывается, и кнопки прячутся или появляются по новым правам.
    const rolesVersion = useRolesStore((state) => state.version)
    const loadRoles = useRolesStore((state) => state.load)
    void rolesVersion

    useEffect(() => {
        if (isAuthenticated) {
            fetchUser().catch(console.error)
        }
    }, [isAuthenticated, fetchUser])

    // Роли правят в настройках на лету - раз в минуту подтягиваем свежие,
    // чтобы интерфейс не расходился с тем, что проверяет сервер.
    useEffect(() => {
        if (!isAuthenticated) return
        loadRoles().catch(console.error)
        const timer = window.setInterval(() => { loadRoles().catch(() => undefined) }, 60000)
        return () => window.clearInterval(timer)
    }, [isAuthenticated, loadRoles])

    if (!isAuthenticated) {
        return (
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        )
    }

    return (
        <Layout>
            <Routes>
                <Route path="/" element={<AllowedRoute allowed={user?.can_view_dashboard}><Dashboard /></AllowedRoute>} />
                {/* Своя ссылка у каждого диалога: её можно открыть в новой вкладке,
                    переслать коллеге, а «назад» в браузере возвращает к прошлому.
                    Один маршрут, а не два: при переходе между диалогами страница
                    не пересоздаётся и не теряет список и фильтры. */}
                <Route path="/chats/:chatId?" element={<LeadChats />} />
                {/* Задачи доступны всем ролям: оператор видит свои, админ - все.
                    Разделение делает сервер, не интерфейс. */}
                <Route path="/tasks" element={<LeadTasksBoard />} />
                <Route path="/broadcast-queue" element={<AllowedRoute allowed={user?.can_view_queue}><BroadcastQueue /></AllowedRoute>} />
                <Route path="/duplicates" element={<AllowedRoute allowed={user?.can_view_duplicates}><Duplicates /></AllowedRoute>} />
                <Route path="/contacts" element={<AllowedRoute allowed={user?.can_view_lead_cards}><Contacts /></AllowedRoute>} />
                <Route path="/contacts/:leadId" element={<AllowedRoute allowed={user?.can_view_lead_cards}><Contacts /></AllowedRoute>} />
                {/* РАЗДЕЛ ОТКЛЮЧЁН вместе со статусной моделью: «Календарь».
                <Route path="/calendar" element={<Calendar />} /> */}
                <Route path="/email/campaigns" element={<EmailCampaigns />} />
                <Route path="/export" element={<AdminOnly><Export /></AdminOnly>} />
                <Route path="/statistics" element={<Statistics />} />
                <Route path="/settings" element={<SettingsOnly><Settings /></SettingsOnly>} />
                <Route path="/account" element={<AccountSettings />} />
                <Route path="/admin/*" element={<AdminOnly><Admin /></AdminOnly>} />
                <Route path="*" element={<Navigate to={user?.can_view_dashboard === false ? '/chats' : '/'} replace />} />
            </Routes>
        </Layout>
    )
}

export default App
